import { Ev } from '../sim/events';
import { FEEL } from '../sim/FeelConstants';
import { Rank } from '../sim/defs';
import { classForRank, type ExplosionClass } from './explosions';
import type { World } from '../sim/World';
import { Hitstop } from './Hitstop';
import { Shake } from './Shake';

/* ═══════════════════════════════════════════════════════════════════════════
   Le metteur en scène.

   Il lit les événements de la simulation et en fait de la SENSATION : gel,
   secousse, flash, recul. Il n'écrit jamais dans le monde — la règle de
   dépendance est à sens unique (PLAN.md §3.1), et c'est ce qui garantit que
   couper tous les effets ne change pas d'un iota le déroulement d'une partie.

   C'est aussi ce qui permet aux sliders d'accessibilité — secousse 0 %,
   flashs 0 % — d'être de véritables réglages de confort et non des options qui
   modifient la difficulté.

   Il vit dans src/game/ : au-dessus de la simulation, en dessous du rendu.
   Math y est libre, puisque rien de ce qu'il calcule n'entre dans un replay.
   ═══════════════════════════════════════════════════════════════════════════ */

export interface FeelToggles {
  /** 0 → 1. Les sliders d'accessibilité du plan (§6.2) sont des multiplicateurs,
   *  jamais des interrupteurs binaires : un joueur sensible au mouvement veut
   *  souvent « moins », pas « rien ». */
  shake: number;
  flash: number;
  hitstop: number;
}

export class Director {
  readonly hitstop = new Hitstop();
  readonly shake = new Shake();

  /** Éclair plein écran, dans [0,1]. Plafonné très bas à l'usage : le plan
   *  fixe une opacité maximale de 0,08 sur 50 ms (§4.6). Un flash franc sur un
   *  fond sombre est une agression, pas un effet. */
  flash = 0;
  /** Recul du mesh joueur, en unités. Jamais de secousse caméra sur le tir :
   *  à 10 volées par seconde, l'écran ne se stabiliserait jamais. */
  recoil = 0;

  readonly toggles: FeelToggles = { shake: 1, flash: 1, hitstop: 1 };

  /** Temps de présentation, en secondes. Distinct du temps de simulation : il
   *  continue d'avancer PENDANT un gel, ce qui est exactement ce qu'on veut —
   *  la secousse et le flash restent vivants alors que le jeu est figé, et
   *  c'est ce contraste qui donne son poids à l'impact. */
  private time = 0;

  /** Lit les événements survenus depuis le dernier appel. À invoquer après
   *  chaque tick, pas une fois par frame : 8 ms de retard sur un hitstop se
   *  sentent. */
  /** Branché par la couche de rendu. Le Director décide QUOI se produit ; le
   *  rendu décide À QUOI ça ressemble. Sans ce branchement, le jeu tourne
   *  exactement pareil — simplement sans effets, ce qui est la preuve que la
   *  frontière tient. */
  onExplosion: ((cls: ExplosionClass, x: number, y: number) => void) | null = null;

  ingest(w: World): void {
    const ev = w.events;
    for (let i = ev.readIndex; i < ev.count; i++) {
      switch (ev.type(i)) {
        case Ev.EnemyKilled: {
          const rank = ev.a(i);
          this.onKill(rank);
          const cls = classForRank(rank);
          this.onExplosion?.(cls, ev.b(i), ev.c(i));
          // Modulation temporaire du bloom sur les grosses explosions : l'une
          // des trois choses qui remplacent les vraies lumières pour 2 % du
          // coût et 90 % du ressenti (PLAN.md §4.6).
          if (cls.lights > 0) this.bloomBoost = 1.3;
          break;
        }
        case Ev.PlayerHit:
          this.request(FEEL.hitstopPlayerHit, FEEL.traumaPlayerHit);
          /* ⚠️ Le réglage s'applique ICI. La version précédente écrivait `1`
             en dur : le curseur de flash existait, était sauvegardé, était
             affiché — et ne faisait rien. Un réglage d'accessibilité inopérant
             est plus grave qu'un réglage absent, parce que le joueur qui en a
             besoin le met à zéro, continue de recevoir les flashs, et en
             conclut que le problème vient de lui. */
          this.flash = this.toggles.flash;
          break;
        default:
          break;
      }
    }
    ev.markRead();
  }

  /** Facteur de bloom courant, retombant vers 1. */
  bloomBoost = 1;

  private onKill(rank: number): void {
    if (rank === Rank.Popcorn) {
      // ⚠️ Aucun gel sur le popcorn. Règle sacrée (§6.2) : à 15 kills/seconde,
      // 22 ms par mort gèleraient près de la moitié du temps de jeu.
      this.request(FEEL.hitstopPopcorn, FEEL.traumaPopcorn);
    } else if (rank === Rank.Heavy) {
      this.request(FEEL.hitstopHeavy, FEEL.traumaHeavy);
    } else {
      this.request(FEEL.hitstopStandard, FEEL.traumaStandard);
    }
  }

  private request(hitstopSec: number, trauma: number): void {
    if (hitstopSec > 0) this.hitstop.request(hitstopSec * 1000 * this.toggles.hitstop);
    if (trauma > 0) this.shake.add(trauma * this.toggles.shake);
  }

  onPlayerFired(): void {
    this.recoil = FEEL.recoil;
  }

  /** Consomme un pas de temps réel. Retourne `true` si la simulation doit être
   *  GELÉE — l'appelant saute alors son `step`. */
  freezeTick(dtMs: number): boolean {
    return this.hitstop.consume(dtMs);
  }

  /** Fait avancer les effets purement visuels. Appelé une fois par frame de
   *  rendu, avec le temps mural — donc y compris pendant un gel. */
  advance(frameMs: number): void {
    const dt = frameMs / 1000;
    this.time += dt;
    this.shake.advance(dt, FEEL.shakeDecay);
    this.flash -= dt * 20; // ~50 ms d'extinction
    if (this.flash < 0) this.flash = 0;
    this.recoil *= 0.82;
    if (this.recoil < 0.01) this.recoil = 0;

    // Retour du bloom vers sa valeur nominale en ~120 ms.
    if (this.bloomBoost > 1) {
      this.bloomBoost -= dt * 2.5;
      if (this.bloomBoost < 1) this.bloomBoost = 1;
    }
  }

  /** Déplacement caméra à appliquer, en unités monde. */
  shakeX(playfieldHeight: number): number {
    return this.shake.offsetX(this.time, FEEL.shakeAmplitude * playfieldHeight);
  }
  shakeY(playfieldHeight: number): number {
    return this.shake.offsetY(this.time, FEEL.shakeAmplitude * playfieldHeight);
  }
  shakeRoll(): number {
    return this.shake.roll(this.time, (FEEL.shakeRollDeg * Math.PI) / 180);
  }

  /** Opacité de l'éclair plein écran, déjà pondérée par le réglage. */
  flashOpacity(): number {
    return this.flash * 0.08 * this.toggles.flash;
  }

  reset(): void {
    this.hitstop.reset();
    this.shake.reset();
    this.flash = 0;
    this.recoil = 0;
  }
}
